import PropTypes from 'prop-types';
import React from 'react';
import PassageLayout from '../../../../common/layouts/passageLayout';
import GoButton from '../../../../common/components/GoButton';
import GoBackButton from '../../../../common/components/GoBackButton';
import StimulusContainer from '../../../../common/splitPageQuestion/AnswerButton/StimulusContainer';
import TextPaneScroll from '../../../../common/textPaneScroll/TextPaneScroll';
import AnswerButtonContainer from '../../../../common/splitPageQuestion/AnswerButton/AnswerButtonContainer';
import AudioContainer from '../../../../common/audio/audioContainer';
import { RCT, COMPREHENSION } from '../../../../constants/measure';
import ScreenErrorContainer from '../../../../common/error/ScreenErrorContainer';
import PassageTitle from '../../../../common/components/PassageTitle';

export default class RCTComprehensionQuestionView extends React.Component {
    constructor(props) {
        super(props);
        this.getTextPaneScroll = this.getTextPaneScroll.bind(this);
        this.getQuestions = this.getQuestions.bind(this);
        this.getBottomNavigation = this.getBottomNavigation.bind(this);
        this.onNext = this.onNext.bind(this);
        this.startScreenSequence = this.startScreenSequence.bind(this);
    }

    componentDidMount() {
        if (!this.props.isDataFetched) {
            this.props.fetchContent();
        } else {
            // Using setTimeout here to introduce a delay here, because the TextPaneScroll
            // component needs to be rendered in the DOM in order to figure out its height.
            // This is needed before the screen sequence is started to enable the text pane
            // scroll and position the scroll bar correctly.
            setTimeout(this.startScreenSequence, 200);
        }
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.isDataFetched && this.props.isDataFetched) {
            this.startScreenSequence();
        }
    }

    componentWillUnmount() {
        this.props.cleanup();
    }

    onNext() {
        return this.props.onNext(this.props.activePassageIndex);
    }

    getSelectedAnswerCallback(questionIndex, responseIndex) {
        return () => {
            this.props.selectAnswer(questionIndex, responseIndex);
        };
    }

    getBottomNavigation() {
        const updatedIndex = 1 + this.props.questionIndex;
        return (
            <div className="comprehension-bottom">
                <GoBackButton
                    onClick={this.props.onBack}
                    isEnabled={this.props.isBackButtonActive} />
                <div className="comprehension-progress">
                    {updatedIndex}
                    {' '}
of
                    {' '}
                    {this.props.totalQuestions}
                </div>
            </div>
        );
    }

    getAnswerOption(answer, index, questionIndex) {
        const audio = answer.responseAudio;
        return (
            <AnswerButtonContainer
                audio={audio}
                index={index}
                text={answer.responseText}
                key={answer.responseId}
                isSelected={(index === this.props.selectedIndex)}
                onAnswerClick={this.getSelectedAnswerCallback(questionIndex, index)}
                isDisabled={this.props.isGreyedOut}
            />
        );
    }

    getResponsesArea(responseOptions, questionIndex) {
        return (
            <div className="vertical-answer-buttons">
                {
                    responseOptions.map((responseOption, index) => this.getAnswerOption(responseOption, index, questionIndex), this)
                }
            </div>
        );
    }

    getQuestion({ id, stimulus, possibleResponses }, index) {
        return (
            <div key={index + id} className={(this.props.questionIndex === index) ? 'visible' : 'hidden'}>
                <div className="comprehension-question">
                    <StimulusContainer
                        key={id}
                        audioTracks={[stimulus.audio]} />
                    <p>{stimulus.text}</p>
                </div>

                <div className="comprehension-answers">
                    {this.getResponsesArea(possibleResponses, index)}
                    {this.getBottomNavigation()}
                </div>
            </div>
        );
    }

    getNextButton() {
        return (
            <div>
                {(this.props.isNextButtonActive)
                    && (
                        <GoButton
                            onClick={this.onNext}
                            isEnabled={!this.props.isScreenBlocked} />
                    )}
            </div>
        );
    }

    getQuestions() {
        return (
            <div className="question-pane">
                {this.props.comprehensionItems.map(this.getQuestion, this)}
                {this.getNextButton()}
            </div>
        );
    }

    getTextPaneScroll() {
        const scrollableContent = this.props.segments.map((segment) => (
            <p key={`seg-${segment.id}`}>
                {segment.text}
            </p>
        ));
        return (
            <div className="text-pane">
                <PassageTitle
                    title={this.props.passageTitle} />
                <TextPaneScroll
                    content={scrollableContent}
                    isEnabled={this.props.isTextPaneScroll}
                    isTabIndexDisabled={this.props.isGreyedOut || this.props.isScreenBlocked}
                />
            </div>

        );
    }

    startScreenSequence() {
        this.props.initializePassage();
        if (this.props.activePassageIndex === 0) {
            this.props.readyScript();
        }
    }

    render() {
        return (
            <PassageLayout
                class={`${RCT}-${COMPREHENSION}`}
                isScreenBlocked={this.props.isScreenBlocked}
                isGreyedOut={this.props.isGreyedOut}>
                <ScreenErrorContainer onRetry={this.onNext} />
                {this.getQuestions()}
                {this.getTextPaneScroll()}
                <AudioContainer />
            </PassageLayout>
        );
    }
}

RCTComprehensionQuestionView.propTypes = {
    isDataFetched: PropTypes.bool,
    isGreyedOut: PropTypes.bool,
    isScreenBlocked: PropTypes.bool,
    isNextButtonActive: PropTypes.bool,
    selectedIndex: PropTypes.number,
    onNext: PropTypes.func,
    onBack: PropTypes.func,
    cleanup: PropTypes.func,
    selectAnswer: PropTypes.func,
    segments: PropTypes.array,
    comprehensionItems: PropTypes.array,
    questionIndex: PropTypes.number,
    totalQuestions: PropTypes.number,
    isBackButtonActive: PropTypes.bool,
    isTextPaneScroll: PropTypes.bool,
    initializePassage: PropTypes.func,
    fetchContent: PropTypes.func,
    readyScript: PropTypes.func,
    passageTitle: PropTypes.string,
    activePassageIndex: PropTypes.number,
};

RCTComprehensionQuestionView.defaultProps = {
    isGreyedOut: false,
    isTextPaneScroll: false,
    isScreenBlocked: false,
    selectedIndex: undefined,
    isNextButtonActive: false,
    isDataFetched: false,
    segments: [],
    comprehensionItems: [],
    questionIndex: 0,
    isBackButtonActive: false,
    totalQuestions: 0,
    initializePassage: () => {},
    cleanup: () => {},
    fetchContent: () => {},
    readyScript: () => {},
    onBack: () => {},
    onNext: () => {},
    selectAnswer: () => {},
    passageTitle: '',
    activePassageIndex: 0,
};
